import { IconArrowUp, IconPlayerStop } from "@tabler/icons-react";
import {
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from "react";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";
import { ButtonVariant } from "../Buttons/button.js";
import { IconButton } from "../Buttons/iconButton.js";
import { Size } from "../helpers/helpers.js";
import { useAutoResizeTextarea } from "../hooks/useAutoResizeTextarea.js";

export interface ChatComposerProps {
  /** Controlled value. When omitted the composer manages its own state. */
  value?: string;
  onValueChange?: (value: string) => void;
  /** Called with the trimmed message when the user submits. */
  onSend: (message: string) => void;
  /** When running, the send button becomes a stop button (if `onStop` set). */
  isRunning?: boolean;
  onStop?: () => void;
  isDisabled?: boolean;
  placeholder?: string;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  /**
   * `inline` (default) is the single-row pill input. `boxed` is the taller
   * card-style composer used by the redesigned research agent, with room for
   * context pills above the input.
   */
  variant?: "inline" | "boxed";
  /** Rendered above the input; `boxed` variant only (e.g. `ChatContextPill`). */
  contextPills?: ReactNode;
  className?: string;
  inputRef?: Ref<HTMLTextAreaElement>;
}

const composerStyle = tv({
  slots: {
    input:
      "block max-h-56 w-full resize-none overflow-y-auto border-none text-foreground placeholder-gray-400 outline outline-1 outline-border-strong transition-shadow duration-150 focus:outline-2 focus:outline-offset-0 focus:outline-primary",
    button:
      "absolute flex items-center justify-center rounded-full text-white disabled:cursor-not-allowed",
    sendIcon: "",
  },
  variants: {
    size: {
      [Size.SMALL]: {
        input: "rounded-xl py-2 pr-10 pl-3 text-sm",
        button: "right-1.5 bottom-1.5 size-6",
        sendIcon: "size-4",
      },
      [Size.MEDIUM]: {
        input: "rounded-2xl py-2 pr-10 pl-4",
        button: "right-1.5 bottom-1.5 size-7",
        sendIcon: "size-5",
      },
    },
  },
  defaultVariants: {
    size: Size.MEDIUM,
  },
});

export const ChatComposer = (props: ChatComposerProps) => {
  const size = props.size ?? Size.MEDIUM;
  const variant = props.variant ?? "inline";
  const style = composerStyle({ size });

  const [uncontrolledValue, setUncontrolledValue] = useState("");
  const value = props.value ?? uncontrolledValue;

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useAutoResizeTextarea({ textareaRef, value });

  // Merge the internal ref (for auto-resize) with an optional caller ref.
  const setTextareaRef = (node: HTMLTextAreaElement | null) => {
    textareaRef.current = node;
    const callerRef = props.inputRef;
    if (typeof callerRef === "function") {
      callerRef(node);
    } else if (callerRef) {
      (callerRef as { current: HTMLTextAreaElement | null }).current = node;
    }
  };

  const setValue = (next: string) => {
    if (props.value !== undefined) {
      props.onValueChange?.(next);
    } else {
      setUncontrolledValue(next);
    }
  };

  const send = () => {
    // Block sends while a response is streaming — the send button is a Stop
    // button in that state, but Enter/submit would otherwise start a second
    // run and interleave responses.
    if (props.isDisabled || props.isRunning) {
      return;
    }

    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return;
    }

    props.onSend(trimmed);
    setValue("");
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    send();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };

  const isEmpty = value.trim().length === 0;
  const showStop = props.isRunning === true && props.onStop !== undefined;

  if (variant === "boxed") {
    return (
      <form
        className={twMerge("relative", props.className)}
        onSubmit={handleSubmit}
      >
        {/* The focus ring belongs to the card, not the textarea: the textarea
            is transparent and fills the card, so ringing it would draw a
            second rectangle inside the first. */}
        <div className="relative min-h-[6.5rem] min-w-0 rounded-lg bg-card px-3.5 py-3.5 shadow-sm ring-1 ring-border-light transition-shadow duration-150 focus-within:ring-2 focus-within:ring-primary">
          {props.contextPills ? (
            <div className="mb-2 flex min-w-0">{props.contextPills}</div>
          ) : null}

          <textarea
            ref={setTextareaRef}
            name="message"
            aria-label="Your message"
            placeholder={props.placeholder ?? "Ask a question..."}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            rows={1}
            className="block max-h-40 min-h-14 w-full min-w-0 resize-none overflow-y-auto border-none bg-transparent p-0 pr-10 text-left text-sm leading-5 text-foreground caret-foreground placeholder:text-muted-foreground focus:ring-0 focus:outline-none"
          />

          {showStop ? (
            <IconButton
              accessibleLabel="Stop response"
              size={Size.MEDIUM}
              variant={ButtonVariant.Primary}
              icon={<IconPlayerStop className="size-4 shrink-0 stroke-[2.5]" />}
              onClick={props.onStop}
              className="absolute right-2 bottom-2"
            />
          ) : (
            <IconButton
              type="submit"
              accessibleLabel="Send message"
              size={Size.MEDIUM}
              variant={ButtonVariant.Primary}
              icon={<IconArrowUp className="size-4 shrink-0 stroke-[2.5]" />}
              isDisabled={isEmpty || props.isDisabled}
              className="absolute right-2 bottom-2"
            />
          )}
        </div>
      </form>
    );
  }

  return (
    <form
      className={twMerge("relative", props.className)}
      onSubmit={handleSubmit}
    >
      <textarea
        ref={setTextareaRef}
        name="message"
        aria-label="Your message"
        placeholder={props.placeholder ?? "Ask anything..."}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        rows={1}
        className={style.input()}
      />

      {showStop ? (
        <button
          type="button"
          aria-label="Stop"
          onClick={props.onStop}
          className={style.button({ class: "bg-gray-600 hover:bg-gray-700" })}
        >
          <IconPlayerStop className={style.sendIcon()} />
        </button>
      ) : (
        <button
          type="submit"
          aria-label="Send message"
          disabled={isEmpty || props.isDisabled}
          className={style.button({
            class: "bg-indigo-800 hover:bg-indigo-900 disabled:bg-disabled",
          })}
        >
          <IconArrowUp className={style.sendIcon()} />
        </button>
      )}
    </form>
  );
};
